// SavePerformanceMonitor.js - Performance monitoring and analytics for auto-save
class SavePerformanceMonitor {
  constructor() {
    this.metrics = {
      saveTimes: [],
      errorRates: [],
      queueSizes: [],
      networkLatency: [],
      userInteractions: []
    };
    
    this.thresholds = {
      slowSave: 5000, // 5 seconds
      verySlowSave: 10000, // 10 seconds
      highErrorRate: 10, // 10%
      largeQueue: 10 // 10 items
    };
    
    this.maxMetricsHistory = 1000;
    this.reportingInterval = 30000; // 30 seconds
    
    this.startReporting();
  }

  /**
   * Track save performance
   * @param {object} saveData - Save performance data
   */
  trackSave(saveData) {
    const {
      subcategoryId,
      changeType,
      duration,
      success,
      error,
      queueSize,
      retryCount = 0
    } = saveData;

    const timestamp = Date.now();
    
    // Track save time
    this.metrics.saveTimes.push({
      subcategoryId,
      changeType,
      duration,
      success,
      error,
      retryCount,
      timestamp
    });

    // Track error rate
    this.metrics.errorRates.push({
      success,
      error,
      changeType,
      timestamp
    });

    // Track queue size
    this.metrics.queueSizes.push({
      size: queueSize,
      timestamp
    });

    // Check for performance issues
    this.checkPerformanceIssues(saveData);

    // Cleanup old metrics
    this.cleanupMetrics();
  }

  /**
   * Track network latency
   * @param {number} latency - Network latency in ms
   * @param {string} endpoint - API endpoint
   */
  trackNetworkLatency(latency, endpoint) {
    this.metrics.networkLatency.push({
      latency,
      endpoint,
      timestamp: Date.now()
    });
  }

  /**
   * Track user interactions
   * @param {string} action - User action
   * @param {object} data - Action data
   */
  trackUserInteraction(action, data) {
    this.metrics.userInteractions.push({
      action,
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Check for performance issues and alert
   * @param {object} saveData - Save data
   */
  checkPerformanceIssues(saveData) {
    const { duration, success, queueSize } = saveData;

    // Check for slow saves
    if (duration > this.thresholds.verySlowSave) {
      this.alertPerformanceIssue('very_slow_save', {
        duration,
        threshold: this.thresholds.verySlowSave,
        subcategoryId: saveData.subcategoryId
      });
    } else if (duration > this.thresholds.slowSave) {
      this.alertPerformanceIssue('slow_save', {
        duration,
        threshold: this.thresholds.slowSave,
        subcategoryId: saveData.subcategoryId
      });
    }

    // Check for high error rate
    const recentErrorRate = this.getRecentErrorRate();
    if (recentErrorRate > this.thresholds.highErrorRate) {
      this.alertPerformanceIssue('high_error_rate', {
        errorRate: recentErrorRate,
        threshold: this.thresholds.highErrorRate
      });
    }

    // Check for large queue
    if (queueSize > this.thresholds.largeQueue) {
      this.alertPerformanceIssue('large_queue', {
        queueSize,
        threshold: this.thresholds.largeQueue
      });
    }
  }

  /**
   * Alert about performance issues
   * @param {string} issueType - Type of issue
   * @param {object} data - Issue data
   */
  alertPerformanceIssue(issueType, data) {
    const alert = {
      type: issueType,
      data,
      timestamp: Date.now(),
      severity: this.getIssueSeverity(issueType)
    };

    // Emit event for UI handling
    const event = new CustomEvent('autosave-performance-issue', {
      detail: alert
    });
    window.dispatchEvent(event);

    // Send to analytics (if available)
    if (window.mixpanel) {
      window.mixpanel.track('Auto-save Performance Issue', {
        issue_type: issueType,
        ...data
      });
    }
  }

  /**
   * Get issue severity
   * @param {string} issueType - Type of issue
   * @returns {string} - Severity level
   */
  getIssueSeverity(issueType) {
    const severityMap = {
      'very_slow_save': 'critical',
      'slow_save': 'warning',
      'high_error_rate': 'critical',
      'large_queue': 'warning'
    };
    
    return severityMap[issueType] || 'info';
  }

  /**
   * Get recent error rate
   * @param {number} timeWindow - Time window in ms (default: 5 minutes)
   * @returns {number} - Error rate percentage
   */
  getRecentErrorRate(timeWindow = 5 * 60 * 1000) {
    const now = Date.now();
    const recentErrors = this.metrics.errorRates.filter(
      error => now - error.timestamp < timeWindow
    );

    if (recentErrors.length === 0) return 0;

    const errorCount = recentErrors.filter(error => !error.success).length;
    return (errorCount / recentErrors.length) * 100;
  }

  /**
   * Get average save time
   * @param {number} timeWindow - Time window in ms
   * @returns {number} - Average save time
   */
  getAverageSaveTime(timeWindow = 5 * 60 * 1000) {
    const now = Date.now();
    const recentSaves = this.metrics.saveTimes.filter(
      save => now - save.timestamp < timeWindow && save.success
    );

    if (recentSaves.length === 0) return 0;

    const totalTime = recentSaves.reduce((sum, save) => sum + save.duration, 0);
    return totalTime / recentSaves.length;
  }

  /**
   * Get performance summary
   * @returns {object} - Performance summary
   */
  getPerformanceSummary() {
    const now = Date.now();
    const last5Minutes = 5 * 60 * 1000;
    const lastHour = 60 * 60 * 1000;

    return {
      // Overall metrics
      totalSaves: this.metrics.saveTimes.length,
      totalErrors: this.metrics.saveTimes.filter(s => !s.success).length,
      overallErrorRate: this.getRecentErrorRate(now),
      
      // Recent performance (last 5 minutes)
      recentErrorRate: this.getRecentErrorRate(last5Minutes),
      recentAverageSaveTime: this.getAverageSaveTime(last5Minutes),
      
      // Hourly performance
      hourlyErrorRate: this.getRecentErrorRate(lastHour),
      hourlyAverageSaveTime: this.getAverageSaveTime(lastHour),
      
      // Current state
      currentQueueSize: this.getCurrentQueueSize(),
      isHealthy: this.isSystemHealthy(),
      
      // Performance trends
      trends: this.getPerformanceTrends()
    };
  }

  /**
   * Get current queue size
   * @returns {number} - Current queue size
   */
  getCurrentQueueSize() {
    const recent = this.metrics.queueSizes.slice(-1);
    return recent.length > 0 ? recent[0].size : 0;
  }

  /**
   * Check if system is healthy
   * @returns {boolean} - Whether system is healthy
   */
  isSystemHealthy() {
    const recentErrorRate = this.getRecentErrorRate();
    const recentAvgTime = this.getAverageSaveTime();
    const currentQueue = this.getCurrentQueueSize();

    return (
      recentErrorRate < this.thresholds.highErrorRate &&
      recentAvgTime < this.thresholds.slowSave &&
      currentQueue < this.thresholds.largeQueue
    );
  }

  /**
   * Get performance trends
   * @returns {object} - Performance trends
   */
  getPerformanceTrends() {
    const now = Date.now();
    const intervals = [
      { name: 'last_5min', window: 5 * 60 * 1000 },
      { name: 'last_15min', window: 15 * 60 * 1000 },
      { name: 'last_hour', window: 60 * 60 * 1000 }
    ];

    const trends = {};
    
    intervals.forEach(interval => {
      const startTime = now - interval.window;
      const intervalSaves = this.metrics.saveTimes.filter(
        save => save.timestamp >= startTime
      );

      trends[interval.name] = {
        saveCount: intervalSaves.length,
        errorRate: intervalSaves.length > 0 
          ? (intervalSaves.filter(s => !s.success).length / intervalSaves.length) * 100 
          : 0,
        averageTime: intervalSaves.length > 0
          ? intervalSaves.reduce((sum, s) => sum + s.duration, 0) / intervalSaves.length
          : 0
      };
    });

    return trends;
  }

  /**
   * Generate performance report
   * @returns {object} - Detailed performance report
   */
  generateReport() {
    const summary = this.getPerformanceSummary();
    const issues = this.getRecentIssues();
    
    return {
      timestamp: new Date().toISOString(),
      summary,
      issues,
      recommendations: this.getRecommendations(summary, issues)
    };
  }

  /**
   * Get recent performance issues
   * @param {number} timeWindow - Time window in ms
   * @returns {Array} - Recent issues
   */
  getRecentIssues(timeWindow = 15 * 60 * 1000) {
    // This would track issues from the alertPerformanceIssue method
    // For now, return empty array - can be enhanced to track issues
    return [];
  }

  /**
   * Get performance recommendations
   * @param {object} summary - Performance summary
   * @param {Array} issues - Recent issues
   * @returns {Array} - Recommendations
   */
  getRecommendations(summary, issues) {
    const recommendations = [];

    if (summary.recentErrorRate > 5) {
      recommendations.push({
        type: 'error_rate',
        message: 'High error rate detected. Check network connectivity and server status.',
        priority: 'high'
      });
    }

    if (summary.recentAverageSaveTime > 3000) {
      recommendations.push({
        type: 'performance',
        message: 'Slow save times detected. Consider optimizing payload size or increasing debounce delay.',
        priority: 'medium'
      });
    }

    if (summary.currentQueueSize > 5) {
      recommendations.push({
        type: 'queue_size',
        message: 'Large save queue detected. Consider increasing concurrent save limit.',
        priority: 'low'
      });
    }

    return recommendations;
  }

  /**
   * Start periodic reporting
   */
  startReporting() {
    setInterval(() => {
      const report = this.generateReport();
      
      // Emit event for external monitoring
      const event = new CustomEvent('autosave-performance-report', {
        detail: report
      });
      window.dispatchEvent(event);
      
    }, this.reportingInterval);
  }

  /**
   * Cleanup old metrics to prevent memory leaks
   */
  cleanupMetrics() {
    Object.keys(this.metrics).forEach(key => {
      if (this.metrics[key].length > this.maxMetricsHistory) {
        this.metrics[key] = this.metrics[key].slice(-this.maxMetricsHistory);
      }
    });
  }

  /**
   * Reset all metrics
   */
  reset() {
    Object.keys(this.metrics).forEach(key => {
      this.metrics[key] = [];
    });
  }

  /**
   * Export metrics for analysis
   * @returns {object} - All metrics data
   */
  exportMetrics() {
    return {
      metrics: this.metrics,
      summary: this.getPerformanceSummary(),
      report: this.generateReport(),
      exportedAt: new Date().toISOString()
    };
  }
}

// Create singleton instance
const savePerformanceMonitor = new SavePerformanceMonitor();

export default savePerformanceMonitor;
